/**
 * Manual journals (`/c/$companyId/journals`): drafts first, then posted ones
 * by date, filtered by state in the address. A row opens its draft page,
 * which links a posted one to its entry.
 */
import type { JSX } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { Boxes, Plus } from "lucide-react";
import {
  Alert,
  Button,
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeader,
  DataTableRow,
  EmptyState,
  Icon,
  PageHeader,
  TableSkeleton,
  Tabs,
  nonEmpty,
} from "@linkhr/design-system";
import { useTranslation } from "@linkhr/i18n";
import { useCompanyBooks, useJournalDrafts } from "../api/ledger";
import { StateChip } from "../components/StateChip";
import { CODES } from "../ledger/books";
import { useLedgerAccess } from "../ledger/useLedgerAccess";
import { useLedgerFormat } from "../ledger/format";
import { NoJournals } from "./JournalEditor";

export const DRAFT_STATES = ["ALL", "DRAFT", "POSTED"] as const;
export type DraftState = (typeof DRAFT_STATES)[number];

export function DraftsScreen({
  companyId,
  state,
  onState,
}: {
  companyId: string;
  state: DraftState;
  onState: (state: DraftState) => void;
}): JSX.Element {
  const { t } = useTranslation("accounting");
  const navigate = useNavigate();
  const access = useLedgerAccess(companyId);
  if (!access.can(CODES.journal)) return <NoJournals />;
  const title = t("accounting:ledger.journal.list.title");
  return (
    <div className="flex flex-col gap-lg">
      <PageHeader
        title={title}
        actions={
          !access.readOnly ? (
            <>
              <Button
                variant="secondary"
                onClick={() =>
                  void navigate({ to: "/c/$companyId/journals/stock-count", params: { companyId } })
                }
              >
                <Icon as={Boxes} size={16} />
                {t("accounting:ledger.journal.nav.stock_count")}
              </Button>
              <Button
                onClick={() =>
                  void navigate({ to: "/c/$companyId/journals/new", params: { companyId } })
                }
              >
                <Icon as={Plus} size={16} />
                {t("accounting:ledger.journal.title_new")}
              </Button>
            </>
          ) : null
        }
      />
      <Tabs.Root
        value={state}
        onValueChange={(next) => onState(DRAFT_STATES.find((s) => s === next) ?? "ALL")}
      >
        <Tabs.List aria-label={title}>
          {/* Literal keys, one per tab, so the static check sees them. */}
          <Tabs.Trigger value="ALL">{t("accounting:ledger.journal.list.filter.ALL")}</Tabs.Trigger>
          <Tabs.Trigger value="DRAFT">
            {t("accounting:ledger.journal.list.filter.DRAFT")}
          </Tabs.Trigger>
          <Tabs.Trigger value="POSTED">
            {t("accounting:ledger.journal.list.filter.POSTED")}
          </Tabs.Trigger>
        </Tabs.List>
        <Tabs.Content value={state} className="pt-md">
          <DraftsTable companyId={companyId} state={state} />
        </Tabs.Content>
      </Tabs.Root>
    </div>
  );
}

function DraftsTable({ companyId, state }: { companyId: string; state: DraftState }): JSX.Element {
  const { t } = useTranslation("accounting");
  const f = useLedgerFormat();
  const drafts = useJournalDrafts(companyId, { kind: "MANUAL", state });
  const currency = useCompanyBooks(companyId).data?.functionalCurrency;
  const linkClass = "rounded-sm font-medium hover:underline focus-visible:focus-ring";
  if (drafts.isError) {
    return (
      <Alert
        variant="danger"
        title={t("accounting:errors.load_title")}
        description={
          <Button size="sm" variant="secondary" onClick={() => void drafts.refetch()}>
            {t("accounting:errors.retry")}
          </Button>
        }
      />
    );
  }
  if (drafts.data === undefined) {
    return (
      <DataTable>
        <TableSkeleton columns={6} aria-label={nonEmpty(t("accounting:loading"))} />
      </DataTable>
    );
  }
  if (drafts.data.length === 0) {
    const heading = t("accounting:ledger.journal.list.empty");
    return (
      <EmptyState
        entities={nonEmpty(heading)}
        nextAction={nonEmpty(heading)}
        heading={heading}
        body={t("accounting:ledger.journal.list.empty_body")}
        illustration="documents"
      />
    );
  }
  return (
    <div className="overflow-x-auto rounded-md border border-subtle">
      <DataTable density="compact">
        <DataTableHeader>
          <tr>
            <DataTableHead>{t("accounting:ledger.journal.list.columns.number")}</DataTableHead>
            <DataTableHead>{t("accounting:ledger.journal.fields.date")}</DataTableHead>
            <DataTableHead>{t("accounting:ledger.journal.fields.description")}</DataTableHead>
            <DataTableHead className="text-end">
              {t("accounting:ledger.journal.list.columns.amount")}
            </DataTableHead>
            <DataTableHead>{t("accounting:ledger.journal.list.columns.created_by")}</DataTableHead>
            <DataTableHead>{t("accounting:ledger.journal.list.columns.state")}</DataTableHead>
          </tr>
        </DataTableHeader>
        <DataTableBody>
          {drafts.data.map((draft) => (
            <DataTableRow key={draft.id}>
              <DataTableCell>
                <Link
                  to="/c/$companyId/journals/$draftId"
                  params={{ companyId, draftId: draft.id }}
                  className={linkClass}
                >
                  {draft.state === "POSTED" ? (
                    <bdi className="font-mono">{draft.number}</bdi>
                  ) : (
                    t("accounting:ledger.journal.number_on_posting")
                  )}
                </Link>
              </DataTableCell>
              <DataTableCell>{f.day(draft.accountingDate)}</DataTableCell>
              <DataTableCell>{draft.description ?? ""}</DataTableCell>
              <DataTableCell className="text-end tabular-nums">
                {currency !== undefined && draft.totalDebit != null
                  ? f.money(draft.totalDebit, currency)
                  : ""}
              </DataTableCell>
              <DataTableCell>{draft.createdBy?.name ?? ""}</DataTableCell>
              <DataTableCell>
                <StateChip family="journal" value={draft.state} />
              </DataTableCell>
            </DataTableRow>
          ))}
        </DataTableBody>
      </DataTable>
    </div>
  );
}
